import React, { useEffect, useRef } from "react";
import { Route, Routes, Navigate } from "react-router-dom";

import "./App.css";

import { useAuth } from "./context/AuthContext";

import DesktopSidebar from "./components/layout/sidebar/DesktopSidebar";
import DesktopPublicNavbar from "./components/layout/navbar/DesktopPublicNavbar";

import LandingPage from "./pages/home/LandingPage";
import HomePage from "./features/home/pages/HomePage";

import VideoWatchPage from "./features/videos/pages/VideoWatchPage";

import MyRentalsPage from "./features/rentals/pages/MyRentalsPage";

import ChannelPage from "./features/channels/pages/ChannelPage";

import GlobalSearchPage from "./features/search/global/pages/GlobalSearchPage";

import {
  MemberPrivateRoute,
  CreatorPrivateRoute,
} from "./routes/PrivateRoutes";

import SplashScreen from "./components/splash/Splash";

import FeedPopsPage from "./features/pops/pages/FeedPopsPage";

import CreatorsPage from "./features/creator/creators/pages/CreatorsPage";
import GroupsPage from "./features/groups/pages/GroupsPage";

import AboutPage from "./pages/about/AboutPage";
import SupportPage from "./pages/support/SupportPage";
import FeaturesPage from "./pages/features/FeaturesPage";

import MemberTermsAndConditionsPage from "./docs/MemberTermsAndConditions";
import GeneralPrivacyPolicy from "./docs/GeneralPrivacyPolicy";

import {
  RugbyVicU16s2025Preview,
  RugbyVicU16s2025Recap,
} from "./pages/blog/sports/Index";

import {
  SignUpPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  VerifyEmailPage,
} from "./features/user/auth/pages/index";

import MemberAccountPage from "./features/user/settings/account/pages/AccountPage";

import UserSavedPopsPage from "./features/pops/pages/UserSavedPopsPage";

import PrivateTalentPage from "./features/talents/private/pages/PrivateTalentPage";
import SharedTalentsPage from "./features/talents/shared/pages/SharedTalentsPage";
import SharedTalentProfilePage from "./features/talents/shared/pages/SharedTalentProfilePage";

import SharedAlbumPhotosPage from "./features/albums/shared/pages/SharedAlbumPhotosPage";

import Error404Page from "./pages/error/Error404";

import { applyPwaScrollLock } from "./utils/helpers/scrolling/pwaScrollFix";

import { useMemberGroups } from "./features/groups/members/hooks/useMemberGroups";

const App = () => {
  const { auth, loading: authLoading } = useAuth();

  const { hasGroupUpdates, checkGlobalGroupUpdates } = useMemberGroups();

  const isAuthenticated = auth?.isAuthenticated === true;
  const user = auth;

  const checkGroupUpdatesRef = useRef(checkGlobalGroupUpdates);

  useEffect(() => {
    checkGroupUpdatesRef.current = checkGlobalGroupUpdates;
  }, [checkGlobalGroupUpdates]);

  useEffect(() => {
    if (authLoading) return;

    const cleanup = applyPwaScrollLock(".app-main-layout");

    return () => {
      cleanup?.();
    };
  }, [authLoading]);

  useEffect(() => {
    if (authLoading || !isAuthenticated) return;

    void checkGroupUpdatesRef.current();
  }, [authLoading, isAuthenticated]);

  if (authLoading) {
    return <SplashScreen />;
  }

  return (
    <main
      className={`app-main-layout ${
        !isAuthenticated ? "app-main-layout--full-width" : ""
      }`}
    >
      <DesktopPublicNavbar isAuthenticated={isAuthenticated} />

      {isAuthenticated && (
        <DesktopSidebar
          isAuthenticated={isAuthenticated}
          user={user}
          hasGroupUpdates={hasGroupUpdates}
        />
      )}

      <Routes>
        <Route
          path="/"
          element={isAuthenticated ? <HomePage /> : <LandingPage />}
        />

        <Route path="/about" element={<AboutPage />} />

        <Route path="/support" element={<SupportPage />} />

        <Route path="/privacy" element={<GeneralPrivacyPolicy />} />

        <Route path="/terms" element={<MemberTermsAndConditionsPage />} />

        <Route path="/features" element={<FeaturesPage />} />

        <Route
          path="/sign-up"
          element={
            !isAuthenticated ? <SignUpPage /> : <Navigate to="/" replace />
          }
        />

        <Route
          path="/sign-in"
          element={
            !isAuthenticated ? <LandingPage /> : <Navigate to="/" replace />
          }
        />

        <Route
          path="/forgot-password"
          element={
            !isAuthenticated ? (
              <ForgotPasswordPage />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />

        <Route
          path="/reset-password/:token"
          element={
            !isAuthenticated ? (
              <ResetPasswordPage />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />

        <Route
          path="/email/verification/:token"
          element={
            !isAuthenticated ? <VerifyEmailPage /> : <Navigate to="/" replace />
          }
        />

        <Route
          path="/sports/rugby-victoria-u16s-2025-preview"
          element={<RugbyVicU16s2025Preview />}
        />

        <Route
          path="/sports/rugby-victoria-u16s-2025-recap"
          element={<RugbyVicU16s2025Recap />}
        />

        <Route element={<MemberPrivateRoute />}>
          <Route path="/search" element={<GlobalSearchPage />} />

          <Route path="/me/profile" element={<MemberAccountPage />} />

          <Route path="/me/pops/saved" element={<UserSavedPopsPage />} />

          <Route path="/me/rentals" element={<MyRentalsPage />} />

          <Route path="/pops" element={<FeedPopsPage />} />

          <Route path="/pops/:popId" element={<FeedPopsPage />} />

          <Route path="/albums/:albumTag" element={<SharedAlbumPhotosPage />} />

          <Route
            path="/groups/:groupTag?"
            element={<GroupsPage userId={user?.id} />}
          />

          <Route path="/me/talents" element={<PrivateTalentPage />} />
          <Route path="/talents" element={<SharedTalentsPage />} />
          <Route
            path="/talents/:talentTag"
            element={<SharedTalentProfilePage />}
          />

          <Route path="/watch/:videoTag" element={<VideoWatchPage />} />

          <Route path="/:creatorusername" element={<ChannelPage />} />
        </Route>

        <Route element={<CreatorPrivateRoute />}>
          <Route
            path="/creators/:username?/:media?"
            element={
              user.isCreator ? (
                <CreatorsPage userId={user?.id} />
              ) : (
                <Error404Page />
              )
            }
          />
        </Route>

        <Route path="*" element={<Error404Page />} />
      </Routes>
    </main>
  );
};

export default App;
